import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'
import * as SelectPrimitive from '@radix-ui/react-select'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'

import { Medias } from '../enumns/medias'
import { useInfiniteScroll } from '../hooks/useInfiniteScroll'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { SelectOptionsSearchInput } from './SelectOptionsSearchInput'

type InfiniteSelectProps = {
  defaultValue?: string
  hasNextPage?: boolean
  options?: Array<{ label: string, value: string }>
  placeholder?: string
  searchInputPlaceholder?: string
  onSearch: (searchTerm: string) => void
  onLoadMoreOptions: () => Promise<unknown>
  onValueChange: (value: string) => void
  disabled?: boolean
}

export const InfiniteSelect = ({
  defaultValue,
  options,
  hasNextPage = false,
  placeholder,
  searchInputPlaceholder,
  onSearch,
  onLoadMoreOptions,
  onValueChange,
  disabled,
}: InfiniteSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [triggerWidth, setTriggerWidth] = useState<number>()
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredOptions, setFilteredOptions] = useState(options)
  const [value, setValue] = useState<string>(String(defaultValue))

  const menuContainerRef = useRef(null)
  const isMinScreenWidthMD = useMediaQuery(`(min-width: ${Medias.MD}px)`)

  useInfiniteScroll(
    menuContainerRef,
    hasNextPage,
    () => onLoadMoreOptions(),
    0,
    {
      enabledAction: isOpen,
    }
  )

  useEffect(() => {
    setFilteredOptions(options)
  }, [options])

  useEffect(() => {
    if (!isOpen) {
      setFilteredOptions(options)
      setSearchTerm('')
    }
  }, [isOpen])

  const handleValueChange = (value: string) => {
    onValueChange(value)
    setValue(value)
  }

  return (
    <SelectPrimitive.Root
      open={isOpen}
      onOpenChange={setIsOpen}
      onValueChange={handleValueChange}
      defaultValue={value || undefined}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger asChild>
        <button
          className="lg:h-[54px] flex items-center justify-between w-full p-3 text-xs rounded-lg outline-none border-grey-500 border-1 text-grey-800 disabled:bg-grey-300 disabled:text-grey-500 lg:py-4 lg:text-sm data-[placeholder]:text-grey-500 data-[placeholder]:font-sans"
          ref={(node) => node && setTriggerWidth(node.offsetWidth)}
        >
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon>
            {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </SelectPrimitive.Icon>
        </button>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Content
        position="popper"
        sideOffset={4}
        className="z-50 overflow-hidden border rounded-md border-primary-light"
        style={{ width: triggerWidth || undefined }}
      >
        <SelectPrimitive.Viewport className="bg-white shadow-lg">
          <SelectPrimitive.Group>
            <div>
              {isMinScreenWidthMD && (
                <SelectOptionsSearchInput
                  placeholder={searchInputPlaceholder}
                  value={searchTerm}
                  onValueChange={(newValue) => setSearchTerm(newValue)}
                  onSearch={() => onSearch(searchTerm)}
                />
              )}

              <div
                className="relative overflow-y-scroll max-h-44 md:max-h-[190px]"
                ref={menuContainerRef}
              >
                {filteredOptions?.length === 0 ? (
                  <p className="flex items-center justify-center h-16 text-xs font-semibold text-center opacity-50 md:text-sm text-grey-700">
                    Nenhum registro encontrado
                  </p>
                ) : (
                  <>
                    {filteredOptions?.map(({ label, value }) => {
                      return (
                        <SelectPrimitive.Item
                          key={value}
                          value={String(value)}
                          onClick={(event) => event.stopPropagation()}
                          className={clsx(
                            'data-[state="checked"]:bg-primary-light h-fit min-h-[40px] font-normal data-[state="checked"]:font-medium data-[state="checked"]:text-white relative flex items-center px-4 py-3 text-xs md:text-sm text-gray-700 ',
                            'radix-disabled:opacity-50',
                            'focus:outline-none cursor-pointer'
                          )}
                        >
                          <SelectPrimitive.ItemText>
                            {label}
                          </SelectPrimitive.ItemText>
                        </SelectPrimitive.Item>
                      )
                    })}
                  </>
                )}
              </div>
            </div>
          </SelectPrimitive.Group>
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Root>
  )
}
